import { useMediaQuery, useTheme } from '@mui/material'
import { Breakpoints } from '@src/utils/useResponsiveSizes'

type UseCompareResponsiveReturn = {
  /**
   * True if the viewport width falls within the range between
   * Breakpoints.MD (960) and Breakpoints.LG (1280), inclusive on relevant sides,
   * i.e., the design considers this as a "medium-to-large desktop" range.
   */
  isBetweenMediumLargeDesktop: boolean
  /**
   * True when the current viewport is not mobile, i.e., width is greater than
   * Breakpoints.SM (600). Equivalent to isDesktop in this hook.
   */
  isDesktop: boolean
  /**
   * True if the viewport width is at or above Breakpoints.XL (1536),
   * i.e., in the extra-large desktop range.
   */
  isExtraLargeDesktop: boolean
  /**
   * True if the viewport width is at or above Breakpoints.LG (1280),
   * i.e., in the large desktop range.
   */
  isLargeDesktop: boolean
  /**
   * True if the viewport width is at or above Breakpoints.MD (960),
   * i.e., in the medium desktop range (and above if not overridden by higher checks).
   */
  isMediumDesktop: boolean
  /**
   * True if the viewport width is below Breakpoints.LG (1280) but above Breakpoints.MD (960),
   * i.e., in the medium-to-small desktop range just below large desktops.
   * This corresponds to widths less than 1280 and greater than or equal to 960.
   */
  isMediumDesktopAndBelow: boolean
  /**
   * True if the viewport width is at or below Breakpoints.SM (600),
   * i.e., in the mobile range.
   */
  isMobile: boolean
  /**
   * True if the viewport width is between Breakpoints.SM (600) and Breakpoints.MD (960),
   * i.e., in the tablet range.
   */
  isTablet: boolean
}

export const useCompareResponsive = (): UseCompareResponsiveReturn => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(Breakpoints.SM))
  const isTablet = useMediaQuery(
    theme.breakpoints.between(Breakpoints.SM, Breakpoints.MD)
  )
  const isMediumDesktop = useMediaQuery(theme.breakpoints.up(Breakpoints.MD))
  const isBetweenMediumLargeDesktop = useMediaQuery(
    theme.breakpoints.between(Breakpoints.MD, Breakpoints.LG)
  )
  const isMediumDesktopAndBelow = useMediaQuery(
    theme.breakpoints.down(Breakpoints.LG)
  )
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up(Breakpoints.LG))
  const isExtraLargeDesktop = useMediaQuery(
    theme.breakpoints.up(Breakpoints.XL)
  )

  return {
    isBetweenMediumLargeDesktop,
    isDesktop: !isMobile,
    isExtraLargeDesktop,
    isLargeDesktop,
    isMediumDesktop,
    isMediumDesktopAndBelow,
    isMobile,
    isTablet,
  }
}
